<template>
  <div>
    <global-confirm-box ref="confirmBox" />
    <warehouse-return-dialog
      v-model="returnDialog"
      :edited-item="editedItem"
      :warehouse="filters.warehouse[0]"
      @groceriesReturned="addDocuments"
      @save="save"
      @success="onSuccess"
    />

    <!-- Mobile View -->
    <template v-if="device.isMobile">
      <template v-if="filteredDocuments.length > 0 || loading">
        <mobile-data-cards
          v-for="(group, warehouseId) in documentsByWarehouse"
          :key="warehouseId"
          :headers="mobileHeaders"
          :items="group.items"
          :loading="loading"
          :title="toLatin(group.warehouseName)"
          item-key="id"
          show-filters
          :fields-per-row="null"
          :show-row-number="false"
          :bold-fields="['number']"
        >
          <template v-slot:item.actions="{ item }">
            <v-row no-gutters justify="end">
              <circle-button
                @click="openDocument(item)"
                icon="mdi-eye"
                :tooltip="$t('overview')"
                color="primary"
                class="mr-2"
              />
              <remove-button @click="deleteItem(item)" />
            </v-row>
          </template>
        </mobile-data-cards>
      </template>

      <empty-state
        v-if="!loading && filteredDocuments.length === 0"
        icon="mdi-backup-restore"
        :title="$t('no_returns')"
        :subtitle="
          lastEntryDate
            ? $t('warehouse.last_return') + ': ' + lastEntryDate
            : $t('no_returns_yet')
        "
      >
        <warehouse-button-return @click="openReturnDialog" />
      </empty-state>

      <div class="fixed-bottom-button" v-if="filteredDocuments.length > 0">
        <v-btn color="pink" block @click="openReturnDialog" dark>
          <v-icon class="mr-2">mdi-backup-restore</v-icon>
          {{ $t("warehouse.return_goods") }}
        </v-btn>
      </div>
    </template>

    <!-- Desktop View -->
    <template v-else>
      <!-- Aggregate View -->
      <template v-if="aggregateMode">
        <v-data-table
          v-if="activeAggregatedItems.length > 0 || loading"
          :headers="aggregateHeaders"
          :items="activeAggregatedItems"
          :loading="loading"
          class="ordera-box-shadow striped-table"
          hide-default-footer
          :items-per-page="-1"
          show-expand
          single-expand
          item-key="grocery_code"
        >
          <template v-slot:top>
            <v-toolbar flat>
              <span v-if="lastEntryDate" class="text-caption grey--text text--darken-1">
                {{ $t("warehouse.last_return") }}:
                <strong>{{ lastEntryDate }}</strong>
              </span>
              <v-spacer />
              <v-btn-toggle v-model="aggregateMode" dense class="mr-3">
                <v-btn :value="true" small>
                  <v-icon left small>mdi-sigma</v-icon>
                  {{ $t("warehouse.aggregate") }}
                </v-btn>
              </v-btn-toggle>
              <warehouse-button-return @click="openReturnDialog" />
            </v-toolbar>
          </template>
          <template v-slot:item.grocery_name="{ item }">
            {{ toLatin(item.grocery_name) || "-" }}
          </template>
          <template v-slot:item.total_quantity="{ item }">
            {{ formatNumber(item.total_quantity) }}
          </template>
          <template v-slot:expanded-item="{ item: grocery }">
            <td :colspan="aggregateHeaders.length" class="pa-0">
              <v-data-table
                :headers="expandedSubHeaders"
                :items="getDocumentsForGroceryWithDetails(grocery.grocery_code)"
                hide-default-footer
                :items-per-page="-1"
                dense
                class="grey lighten-5 elevation-0"
              >
                <template v-slot:item.document_date="{ item }">
                  {{ localeDate(item.document_date) }}
                </template>
                <template v-slot:item.warehouse.name="{ item }">
                  {{ toLatin(item.warehouse && item.warehouse.name) || "-" }}
                </template>
                <template v-slot:item.grocery_quantity="{ item }">
                  {{ formatNumber(item.grocery_quantity) }} {{ item.grocery_measure }}
                </template>
                <template v-slot:item.grocery_value="{ item }">
                  {{ formatNumber(item.grocery_value) }}
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-row no-gutters justify="end">
                    <circle-button @click="openDocument(item)" icon="mdi-eye" :tooltip="$t('overview')" color="primary" class="mr-2" />
                    <remove-button @click="deleteItem(item)" />
                  </v-row>
                </template>
              </v-data-table>
            </td>
          </template>
        </v-data-table>
        <empty-state
          v-if="!loading && activeAggregatedItems.length === 0"
          icon="mdi-sigma"
          :title="$t('warehouse.no_aggregate_data')"
          :subtitle="$t('warehouse.no_entries_for_period')"
        />
      </template>

      <!-- Regular Document View -->
      <v-data-table
        v-else-if="displayedDocuments.length > 0 || loading"
        :headers="headers"
        :items="displayedDocuments"
        :loading="loading"
        class="ordera-box-shadow striped-table"
      >
        <template v-slot:top>
          <v-toolbar flat>
            <span v-if="lastEntryDate" class="text-caption grey--text text--darken-1">
              {{ $t("warehouse.last_return") }}:
              <strong>{{ lastEntryDate }}</strong>
            </span>
            <v-spacer />
            <v-btn-toggle v-model="aggregateMode" dense class="mr-3">
              <v-btn :value="true" small>
                <v-icon left small>mdi-sigma</v-icon>
                {{ $t("warehouse.aggregate") }}
              </v-btn>
            </v-btn-toggle>
            <warehouse-button-return @click="openReturnDialog" />
          </v-toolbar>
        </template>
        <template v-slot:item.warehouse.name="{ item }">
          {{ toLatin(item.warehouse?.name) || "-" }}
        </template>
        <template v-slot:item.document_date="{ item }">
          {{ localeDate(item.document_date) }}
        </template>
        <template v-slot:item.created_at="{ item }">
          {{
            localeDate(item.created_at, {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })
          }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-row no-gutters justify="end">
            <circle-button
              @click="openDocument(item)"
              icon="mdi-eye"
              :tooltip="$t('overview')"
              color="primary"
              class="mr-2"
            />
            <remove-button @click="deleteItem(item)" />
          </v-row>
        </template>
      </v-data-table>

      <empty-state
        v-if="!aggregateMode && !loading && displayedDocuments.length === 0"
        icon="mdi-backup-restore"
        :title="$t('no_returns')"
        :subtitle="
          lastEntryDate
            ? $t('warehouse.last_return') + ': ' + lastEntryDate
            : $t('no_returns_yet')
        "
        @click:subtitle="setFiltersToLastEntry()"
      >
        <warehouse-button-return @click="openReturnDialog" />
      </empty-state>
    </template>
  </div>
</template>

<script>
import { WarehouseButtonReturn } from "@/components/warehouse/buttons";
import WarehouseReturnDialog from "@/components/warehouse/dialogs/WarehouseReturnDialog.vue";
import { returnDocumentGrocery } from "@/models/warehouse";
import EmptyState from "@/components/global/EmptyState.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import warehouseDocumentTabMixin from "@/mixins/warehouse/warehouseDocumentTabMixin";
import warehouseService from "@/services/warehouse";

export default {
  components: {
    WarehouseButtonReturn,
    WarehouseReturnDialog,
    EmptyState,
    MobileDataCards,
  },
  mixins: [warehouseDocumentTabMixin],
  data: () => ({
    documentType: "return",
    returnDialog: false,
    editedItem: returnDocumentGrocery,
  }),

  created() {
    this.load();
  },

  methods: {
    onSuccess() {
      this.load();
      this.$store.commit("warehouse/TRIGGER_REFRESH");
    },
    addDocuments(documents) {
      this.documents.push(...documents);
      this.showSuccessSnackbar(this.$t("return_documents_added"));
    },
    openReturnDialog() {
      this.editedItem = Object.assign(
        {},
        { ...returnDocumentGrocery, groceries: [], warehouse: this.filters.warehouse[0] }
      );
      this.returnDialog = true;
    },

    openDocument(item) {
      this.editedItem = Object.assign({}, item);
      this.returnDialog = true;
    },

    async deleteItem(item) {
      const confirmed = await this.$refs.confirmBox
        .open(
          this.$t("warehouse.delete_return"),
          this.$t("warehouse.confirm_delete_return"),
          {
            color: "warning",
          }
        )
        .catch(() => false);

      if (confirmed) {
        try {
          await warehouseService.removeWarehouse(item.id);
          this.documents = this.documents.filter((i) => i.id !== item.id);
          this.showSuccessSnackbar(this.$t("return_document_deleted"));
        } catch (err) {
          this.showErrorSnackbar(this.$t("error_deleting_return_document"));
          console.warn(err);
        }
      }
    },

    async save() {
      try {
        if (this.editedItem.id) {
          const res = await warehouseService.updateWarehouse(
            this.editedItem.id,
            this.editedItem
          );
          const index = this.documents.findIndex(
            (item) => item.id === this.editedItem.id
          );
          this.documents[index] = res.data;
          this.showSuccessSnackbar(this.$t("return_document_updated"));
        } else {
          const res = await warehouseService.createWarehouse(this.editedItem);
          this.documents.push(res.data);
          this.showSuccessSnackbar(this.$t("return_document_created"));
        }
      } catch (e) {
        this.showErrorSnackbar(this.$t("error_saving_return_document"));
      }
      this.returnDialog = false;
    },
  },

  computed: {
    headers() {
      const multiWarehouse = this.filters.warehouse.length > 1;

      return [
        { text: "ID", value: "id", mobile: false },
        {
          text: this.$t("stock"),
          value: "warehouse.name",
          mobile: false,
          show: multiWarehouse,
        },
        { text: this.$t("warehouse.document_number"), value: "number", cols: 6 },
        { text: this.$t("warehouse.return_date"), value: "document_date", cols: 6 },
        { text: this.$t("note"), value: "notes", cols: 9 },
        { text: this.$t("status_created"), value: "created_at", mobile: false, cols: 9 },
        {
          text: this.$t("actions"),
          value: "actions",
          sortable: false,
          print: false,
          cols: 3,
          width: "100px",
          align: "right",
        },
      ].filter((h) => h.show !== false);
    },
    exportTitle() {
      const parts = [
        this.filters.warehouse.map((warehouse) => warehouse.name).join(", ") ||
          this.$t("stock"),
        this.$t("warehouse.return").toLowerCase(),
      ];
      if (this.aggregateMode) parts.push(this.$t("warehouse.aggregate"));
      return parts.join(" ");
    },
  },
};
</script>

<style scoped>
.fixed-bottom-button {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px;
  background: white;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
  z-index: 5;
}
</style>
